import { Avatar, Text } from "@fluentui/react-northstar";
import React, { useMemo } from "react";
import {
  formatDate,
  parseIfJSON,
  splitCamelCase,
} from "../../shared/utils/helper";
import ChangedIcon from "../../../images/print/changed-icon.png";
import AddedIcon from "../../../images/print/added-icon.png";
import TransitionedIcon from "../../../images/print/transition.png";
import RenamedIcon from "../../../images/print/rename.png";
import ApprovedIcon from "../../../images/print/approved.png";
import RejectedIcon from "../../../images/print/rejected.png";
import MergedIcon from "../../../images/print/merged.png";
import RelatedIcon from "../../../images/print/related.png";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ConvertDateToLocale } from "../../shared/common/ConvertDate";
import { groupActivitiesByDay } from "../../shared/components/TicketConversation/activityGrouping";
import { TFunction } from "i18next";
import { LockSolidIcon } from "@fluentui/react-icons-mdl2";
import { ActivityItemProps } from "../../shared/components/TicketConversation/TicketConversation.interfaces";

type TicketComment = {
  Id: number;
  ModifiedDate: string; // ISO date string
  ModifiedById: number;
  CreatedDate: string; // ISO date string
  CreatedById: number;
  Guid: string; // UUID
  IsDeleted: boolean;
  Body: string;
  IsPublic: boolean;
  TicketId: number;
  AllowedRoles: string[];
  FileAttachments: any[];
};

type ModifierInfo = {
  Name: string;
  Id: number;
  Email: string;
};

type Actor = {
  PlatformUserId: string;
  PlatformUser?: PlatformUser;
  TicketId: string;
  Id: string;
  ModifiedDate: string; // e.g. "28/07/2025 10:26:23 +00:00"
  ModifiedById: string;
  CreatedDate: string;
  CreatedById: string;
  Guid: string;
  IsDeleted: string; // "True" or "False"
};

type Action =
  | "created"
  | "added"
  | "changed"
  | "removed"
  | "commented"
  | "attached"
  | "assigned"
  | "renamed"
  | "transitioned"
  | "declined"
  | "approved"
  | "related"
  | "merged";

const actionIcons: Partial<Record<Action, string>> = {
  added: AddedIcon,
  changed: ChangedIcon,
  transitioned: TransitionedIcon,
  assigned: ChangedIcon,
  removed: ChangedIcon,
  attached: ChangedIcon,
  renamed: RenamedIcon,
  approved: ApprovedIcon,
  declined: RejectedIcon,
  merged: MergedIcon,
  related: RelatedIcon,
};

type AddedRemovedFieldProps = {
  field: string;
  value: string;
  platformUsers?: PlatformUser[];
};

const AddedRemovedField: React.FC<AddedRemovedFieldProps> = ({
  field,
  value,
  platformUsers,
}) => {
  let printValue = value;
  if (
    field === "AffectedUsers" ||
    field === "TicketCollaborators" ||
    field === "TaskCollaborators"
  ) {
    const addedActor = parseIfJSON(value);
    const user = platformUsers.find(u => u.Id === parseInt(addedActor?.PlatformUserId));
    printValue = user?.FullName ?? value;
  } else if (field === "Tag") {
    printValue = parseIfJSON(value).Name ?? value;
  } else if (field === "FileAttachment") {
    printValue = parseIfJSON(value).FileName ?? value;
  }
  return (
    <Text weight="semibold" size="small">
      {printValue}
    </Text>
  );
};

const fieldToString = (
  field: string,
  t: TFunction<"translation", undefined, "translation">
): string => {
  if (field === "CardAnswerJson") {
    return t("ticket-details.ticket-conversation.field.card-answer");
  } else {
    return splitCamelCase(field);
  }
};

const ChangedField: React.FC<AddedRemovedFieldProps> = ({ field, value }) => {
  const { t } = useTranslation();
  let printValue = value;
  if (field === "TicketTasks") {
    printValue = parseIfJSON(value).title;
  } else if (field === "DueDate") {
    printValue = ConvertDateToLocale(value, DateTime.DATE_MED_WITH_WEEKDAY);
  } else if (field === "ResolutionDate") {
    printValue = ConvertDateToLocale(value, DateTime.DATE_MED_WITH_WEEKDAY);
  } else if (field === "Requester") {
    printValue = parseIfJSON(value).Name;
  }
  return (
    <>
      {field === "CardAnswerJson" || field === "CardTemplateJson" ? (
        <Text weight="light" size="small">
          {" "}
          {t("ticket-details.ticket-conversation.actions.changed")}{" "}
          {fieldToString(field, t)}
        </Text>
      ) : (
        <>
          <Text weight="light" size="small">
            {" "}
            {t("ticket-details.ticket-conversation.actions.changed")}{" "}
            {splitCamelCase(field)}
            {t("ticket-details.ticket-conversation.to")}{" "}
          </Text>{" "}
          <Text weight="semibold" size="small">
            {printValue}
          </Text>
        </>
      )}
    </>
  );
};

type PrintActivityItemProps = {
  activity: ActivityItemProps;
  previousAction?: ActivityItemProps;
  isPublic?: boolean;
  platformUsers: PlatformUser[];
};

// Memoized component for individual activity items
const PrintActivityItem: React.FC<PrintActivityItemProps> = ({
  activity,
  previousAction,
  isPublic,
  platformUsers,
}) => {
  const { t } = useTranslation();
  const prevAction = previousAction?.Action
    ? (previousAction.Action as Action)
    : null;
  const modifier = useMemo(() => {
    return (parseIfJSON(activity.ModifierInfo) ?? {
      Name: "Unknown",
      Email: "",
    }) as ModifierInfo;
  }, [activity.ModifierInfo]);

  const action: Action = activity.Action as Action;
  switch (action) {
    case "created":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <Avatar name={modifier?.Name} className="avatar" size="smaller" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            {activity.Field === "Init" && (
              <>
                <Text weight="light" size="small">
                  {" "}
                  {t("ticket-details.ticket-conversation.actions.created")}{" "}
                </Text>{" "}
                <Text weight="semibold" size="small">
                  {t("ticket-details.ticket-conversation.created")}
                  {activity.TicketId}
                </Text>
              </>
            )}
            {activity.Field === "TicketTasks" && (
              <>
                <Text weight="light" size="small">
                  {" "}
                  {t("ticket-details.ticket-conversation.actions.created")}{" "}
                  {t("ticket-details.ticket-conversation.actions.task")}{" "}
                </Text>{" "}
                <Text weight="semibold" size="small">
                  {activity.Value}
                </Text>
              </>
            )}
            {activity.Field !== "TicketTasks" && activity.Field !== "Init" && (
              <>
                <Text weight="light" size="small">
                  {" "}
                  {t("ticket-details.ticket-conversation.actions.created")}{" "}
                  {t("ticket-details.ticket-conversation.actions.task")}{" "}
                </Text>{" "}
                <Text weight="semibold" size="small">
                  {activity.Value}
                </Text>
              </>
            )}
          </div>
        </div>
      );
    case "added":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="added icon" width="24px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text size="small" weight="light">
              {" "}
              {t("ticket-details.ticket-conversation.actions.added")}{" "}
              {splitCamelCase(activity.Field)}
            </Text>{" "}
            <AddedRemovedField field={activity.Field} value={activity.Value} platformUsers={platformUsers}/>
          </div>
        </div>
      );
    case "related":
      return (
        <div className="flex items-center gap-1 flex-wrap no-break">
          <img src={actionIcons[action]} alt="added icon" width="24px" />
          <Text size="small" weight="semibold" className="ml-1">
            {modifier.Name}
          </Text>
          <Text size="small">
            {` ${t("ticket-details.ticket-conversation.actions.related")} ${t(
              "ticket-details.edit-ticket-form.relate-ticket.ticket-selection.merge-confirmation.ticket"
            )} `}
          </Text>
          <Text
            weight="bold"
            size="small"
            content={activity?.RelatedTicketIds}
          />
        </div>
      );
    case "merged":
      return (
        <div className="flex items-center gap-1 flex-wrap no-break">
          <img src={actionIcons[action]} alt="added icon" width="24px" />
          <Text size="small" weight="semibold" className="ml-1">
            {modifier.Name}
          </Text>
          <Text size="small">
            {`${t("ticket-details.ticket-conversation.actions.merged")} ${t(
              "ticket-details.edit-ticket-form.relate-ticket.ticket-selection.merge-confirmation.ticket"
            )}`}
          </Text>
          <Text
            weight="bold"
            size="small"
            content={activity?.RelatedTicketIds}
          />
        </div>
      );
    case "changed":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="changed icon" width="24px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <ChangedField field={activity.Field} value={activity.Value} />
          </div>
        </div>
      );
    case "commented":
      const comment = parseIfJSON(activity.Value) as TicketComment;
      if (comment.Body.trim() === "<tikit-comment-empty></tikit-comment-empty>")
        return null;
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <Avatar name={modifier?.Name} className="avatar" size="smaller" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text size="small" className="ml-1">
              {formatDate(comment.CreatedDate)}
            </Text>
            {!comment.IsPublic && (
              <LockSolidIcon className="text-private print-icon h-2 w-2" />
            )}
          </div>
          <div className="pl-8 mt-1">
            <ReactMarkdown>{comment.Body.replace(/\n/g, "  \n")}</ReactMarkdown>
          </div>
        </div>
      );
    case "transitioned":
      const transitioned: TicketLifecyclePhase =
        parseIfJSON(activity.Value) ?? ({} as TicketLifecyclePhase);
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img
              src={actionIcons[action]}
              alt="transitioned icon"
              width="24px"
              className="p-1"
            />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {" "}
              {t(
                "ticket-details.ticket-conversation.actions.lifecycle-transitioned-phase"
              )}{" "}
              {splitCamelCase(activity.Field)}
            </Text>{" "}
            <Text weight="semibold" size="small">
              {transitioned?.Name ?? ""}
            </Text>
          </div>
        </div>
      );
    case "assigned":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="assigned icon" width="24px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t(
                "ticket-details.ticket-conversation.actions.assigned"
              )} ${t("ticket-details.ticket-conversation.to")} `}
            </Text>
            <Text weight="semibold" size="small">
              {parseIfJSON(activity.Value).Name}
            </Text>
          </div>
        </div>
      );
    case "renamed":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img
              src={actionIcons[action]}
              alt="renamed icon"
              width="24px"
              style={{ padding: "3px" }}
            />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t(
                "ticket-details.ticket-conversation.actions.renamed"
              )} ${splitCamelCase(activity.Field)} ${t(
                "ticket-details.ticket-conversation.to"
              )} `}
            </Text>
            <Text weight="semibold" size="small">
              {activity.Value}
            </Text>
          </div>
        </div>
      );
    case "removed":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="removed icon" width="24px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t("ticket-details.ticket-conversation.actions.removed")} `}
            </Text>
            <AddedRemovedField field={activity.Field} value={activity.Value} />
          </div>
        </div>
      );
    case "attached":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <Avatar name={modifier?.Name} className="avatar" size="smaller" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t("ticket-details.ticket-conversation.actions.attached")} `}
            </Text>
            <Text weight="semibold" size="small">
              {parseIfJSON(activity.Value).FileName}
            </Text>
            {!isPublic &&
              (prevAction === "commented" || prevAction === "attached") && (
                <LockSolidIcon className="text-private print-icon h-2 w-2" />
              )}
          </div>
        </div>
      );
    case "declined":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="rejected icon" height="16px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t("ticket-details.ticket-conversation.actions.declined")} `}
            </Text>
            <Text weight="semibold" size="small">
              {activity.Value}
            </Text>
          </div>
        </div>
      );
    case "approved":
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <img src={actionIcons[action]} alt="approved icon" height="16px" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${t("ticket-details.ticket-conversation.actions.approved")} `}
            </Text>
            <Text weight="semibold" size="small">
              {activity.Value}
            </Text>
          </div>
        </div>
      );
    default:
      return (
        <div className="my-2">
          <div className="flex items-center gap-1 flex-wrap no-break">
            <Avatar name={modifier?.Name} className="avatar" size="smaller" />
            <Text size="small" weight="semibold" className="ml-1">
              {modifier.Name}
            </Text>
            <Text weight="light" size="small">
              {` ${action} `}
            </Text>
            <Text weight="semibold" size="small">
              {activity.Value}
            </Text>
          </div>
        </div>
      );
  }
};

type TicketActivityPrintProps = {
  activities: ActivityItemProps[];
  platformUsers: PlatformUser[];
};

export const TicketActivityPrint: React.FC<TicketActivityPrintProps> = ({
  activities,
  platformUsers,
}) => {
  const { t } = useTranslation();
  // Memoize the grouped activities to prevent recalculation on every render
  const grouped = useMemo(() => groupActivitiesByDay(activities), [activities]);
  let isPublic = false;
  return (
    <>
      <div className="hidden print-block activity print-text-color">
        <div>
          <Text weight="semibold" className="mb-1">
            {t("ticket-details.menu.activity")}
          </Text>
        </div>
        {grouped.map(({ key, label, items: activity }) => (
          <div key={key}>
            <div className="flex justify-center">
              <Text size="small" weight="light" className="my-1">
                {label}
              </Text>
            </div>
            <div className="mt-2">
              {activity.map((item, index) => {
                const prevItem = index > 0 ? activity[index - 1] : null;
                if (item?.Action === "commented") {
                  const comment = item.Value
                    ? (parseIfJSON(item.Value) as TicketComment)
                    : null;
                  isPublic = comment ? comment.IsPublic : false;
                }
                return (
                  <div key={item.Id}>
                    <PrintActivityItem
                      activity={item}
                      previousAction={prevItem}
                      isPublic={isPublic}
                      platformUsers={platformUsers}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
